// src/config/permissions/config.ts

import { ResourcesMap, AdvancedResourceConfig } from './types';

/**
 * Configuración centralizada de permisos para todos los recursos
 * Define qué roles pueden realizar qué acciones en cada recurso
 */
export const PERMISSIONS_CONFIG: ResourcesMap = {
  // ============================================
  // DASHBOARD
  // ============================================
  dashboard: {
    name: 'dashboard',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
    },
  },

  // ============================================
  // GRUPOS
  // ============================================
  groups: {
    name: 'groups',
    permissions: {
      access: ['SuperAdmin'],
      list: ['SuperAdmin'],
      show: ['SuperAdmin', 'GroupAdmin'], // GroupAdmin puede ver su grupo
      create: ['SuperAdmin'],
      edit: ['SuperAdmin'],
      delete: ['SuperAdmin'],
      export: ['SuperAdmin'],
    },
    customPermissions: {
      manageHotels: ['SuperAdmin', 'GroupAdmin'],
      viewStats: ['SuperAdmin', 'GroupAdmin'],
    },
  },

  // ============================================
  // USUARIOS
  // ============================================
  users: {
    name: 'users',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'], // Receptionist puede ver su perfil
      create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      export: ['SuperAdmin', 'GroupAdmin'],
    },
    customPermissions: {
      changeRole: ['SuperAdmin', 'GroupAdmin'],
      resetPassword: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      impersonate: ['SuperAdmin'],
    },
  },

  // ============================================
  // HOTELES
  // ============================================
  hotels: {
    name: 'hotels',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin'],
      list: ['SuperAdmin', 'GroupAdmin'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'], // HotelAdmin ve su hotel
      create: ['SuperAdmin', 'GroupAdmin'],
      edit: ['SuperAdmin', 'GroupAdmin'],
      delete: ['SuperAdmin'],
      export: ['SuperAdmin', 'GroupAdmin'],
    },
    customPermissions: {
      manageZones: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      viewStats: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      configureSettings: ['SuperAdmin', 'GroupAdmin'],
    },
  },

  // ============================================
  // ZONAS
  // ============================================
  zones: {
    name: 'zones',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
    },
  },

  // ============================================
  // TAQUILLAS
  // ============================================
  lockers: {
    name: 'lockers',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      export: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
    },
    customPermissions: {
      forceOpen: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      changeStatus: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      viewHistory: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
    },
  },

  // ============================================
  // TIPOS DE TAQUILLA
  // ============================================
  'locker-types': {
    name: 'locker-types',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin'],
      list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      create: ['SuperAdmin', 'GroupAdmin'],
      edit: ['SuperAdmin', 'GroupAdmin'],
      delete: ['SuperAdmin', 'GroupAdmin'],
    },
  },

  // ============================================
  // CONTROLADORES DE TAQUILLAS
  // ============================================
  'locker-controllers': {
    name: 'locker-controllers',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      create: ['SuperAdmin', 'GroupAdmin'],
      edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      delete: ['SuperAdmin', 'GroupAdmin'],
    },
    customPermissions: {
      restart: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      updateFirmware: ['SuperAdmin'],
      viewLogs: ['SuperAdmin', 'GroupAdmin'],
    },
  },

  // ============================================
  // HMIs (Interfaces Humano-Máquina)
  // ============================================
  hmis: {
    name: 'hmis',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      create: ['SuperAdmin', 'GroupAdmin'],
      edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      delete: ['SuperAdmin', 'GroupAdmin'],
    },
    customPermissions: {
      configure: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      viewStats: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
    },
  },

  // ============================================
  // ASIGNACIONES
  // ============================================
  assignments: {
    name: 'assignments',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      export: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
    },
    customPermissions: {
      forceRelease: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      extendTime: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      viewHistory: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      manageTags: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
    },
  },

  // ============================================
  // ETIQUETAS
  // ============================================
  tags: {
    name: 'tags',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
    },
    customPermissions: {
      activate: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      deactivate: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      reassign: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
    },
  },

  // ============================================
  // TIPOS DE ETIQUETA
  // ============================================
  'tag-types': {
    name: 'tag-types',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin'],
      list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      create: ['SuperAdmin'],
      edit: ['SuperAdmin'],
      delete: ['SuperAdmin'],
    },
  },

  // ============================================
  // REPORTES
  // ============================================
  reports: {
    name: 'reports',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      export: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
    },
    customPermissions: {
      viewFinancial: ['SuperAdmin', 'GroupAdmin'],
      viewOperational: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      viewOccupancy: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
      schedule: ['SuperAdmin', 'GroupAdmin'],
    },
  },

  // ============================================
  // CONFIGURACIÓN
  // ============================================
  settings: {
    name: 'settings',
    permissions: {
      access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
    },
    customPermissions: {
      manageSystem: ['SuperAdmin'],
      manageGroup: ['SuperAdmin', 'GroupAdmin'],
      manageHotel: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
      manageIntegrations: ['SuperAdmin', 'GroupAdmin'],
      manageBilling: ['SuperAdmin'],
    },
  },
};

/**
 * Configuración avanzada con permisos condicionales
 * Por ejemplo: un usuario solo puede editar su propio perfil
 */
export const ADVANCED_PERMISSIONS: Partial<Record<keyof ResourcesMap, AdvancedResourceConfig>> = {
  users: {
    ...PERMISSIONS_CONFIG.users,
    conditionalPermissions: {
      edit: [
        {
          // Un usuario puede editar su propio perfil
          roles: ['Receptionist', 'User'],
          condition: (context) => context.user.id === context.resource?.id,
        },
      ],
      show: [
        {
          // Un usuario puede ver su propio perfil
          roles: ['Receptionist', 'User'],
          condition: (context) => context.user.id === context.resource?.id,
        },
      ],
    },
  },
  
  hotels: {
    ...PERMISSIONS_CONFIG.hotels,
    conditionalPermissions: {
      show: [
        {
          // HotelAdmin solo puede ver su propio hotel
          roles: ['HotelAdmin'],
          condition: (context) => context.user.hotelId === context.resource?.id,
        },
      ],
      edit: [
        {
          // HotelAdmin puede editar configuración básica de su hotel
          roles: ['HotelAdmin'],
          condition: (context) => context.user.hotelId === context.resource?.id,
        },
      ],
    },
  },
  
  groups: {
    ...PERMISSIONS_CONFIG.groups,
    conditionalPermissions: {
      show: [
        {
          // GroupAdmin solo puede ver su propio grupo
          roles: ['GroupAdmin'],
          condition: (context) => context.user.groupId === context.resource?.id,
        },
      ],
    },
  },
};

/**
 * Helper para obtener permisos de un recurso
 */
export const getResourcePermissions = (
  resourceName: keyof ResourcesMap
): ResourcesMap[keyof ResourcesMap] => {
  return PERMISSIONS_CONFIG[resourceName];
};

/**
 * Helper para verificar si un rol tiene permiso para una acción
 */
export const roleHasPermission = (
  role: string,
  resource: keyof ResourcesMap,
  action: string
): boolean => {
  const resourceConfig = PERMISSIONS_CONFIG[resource];
  if (!resourceConfig) return false;
  
  const allowedRoles = resourceConfig.permissions[action] || [];
  return allowedRoles.includes(role as any);
};

/**
 * Exportar todo para uso en la aplicación
 */
export default PERMISSIONS_CONFIG;